import type { VoidFunctionComponent } from 'react';
import type {
  SBOMAnalysisDependencyScan,
  SBOMAnalysisDetails,
  SBOMAnalysisFile,
  SBOMAnalysisRequiredFields, SBOMAnalysisStatus,
} from '../../../../../shared/sbomSummary/sbomAnalysis';
import SBOMAnalysisItem from './SBOMAnalysisItem';
import Flex from '../../../generic/Flex';
import { SBOMAnalysisCategory } from './SBOMAnalysisComponent';
import styles from './SBOMAnalysis.module.less';
import { Icon, icons } from '../../../generic';
import SummaryActionLink from '../SummaryActionLink';
import { BannerAttention, BannerSize, BannerStyle } from '../../../../../typings/enums';
import Banner from '../../../generic/Banner';

type SBOMAnalysisSectionProps = {
  category: SBOMAnalysisCategory;
  data: SBOMAnalysisFile | SBOMAnalysisRequiredFields | SBOMAnalysisDependencyScan;
  revisionBaseUrl?: string;
  testId?: string;
};

const SBOMAnalysisSection: VoidFunctionComponent<SBOMAnalysisSectionProps> = ({
  category,
  data,
  revisionBaseUrl,
  testId,
}) => {
  if (!data || !data.analysis) {
    return null;
  }

  const count = data.analysis.length;
  const grid = count === 1 ? 'col-lg-12' : 'col-lg-6';

  return (
    <div className="width-full" data-testid={testId}>
      <Flex justify="flex-start" gap={5} className={styles.sectionTitle}>
        <SectionIcon category={category} />
        <SectionTitle category={category} />
        <SectionLink category={category} revisionBaseUrl={revisionBaseUrl} />
      </Flex>
      <hr className="margin-top-sm margin-bottom-sm" />
      <SectionBanner category={category} status={data.status} />
      <div className="row">
        {data.analysis.map((analysisDetails: SBOMAnalysisDetails) => {
          return (
            <div className={grid} key={analysisDetails.description}>
              <SBOMAnalysisItem item={analysisDetails} />
              {count > 1 && <hr className="nomargin-v" />}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SBOMAnalysisSection;

/**
 * Analysis Section: Icon Component
 */
type SectionIconProps = { category: SBOMAnalysisCategory };
const SectionIcon: VoidFunctionComponent<SectionIconProps> = ({
  category,
}) => {
  const getIcon = () => {
    switch (category) {
      case SBOMAnalysisCategory.FILE:
        return icons.listTree;
      case SBOMAnalysisCategory.REQUIRED_FIELDS:
        return icons.tableTree;
      case SBOMAnalysisCategory.DEPENDENCY_SCAN:
      default:
        return icons.box;
    }
  };
  return (<Icon icon={getIcon()} />);
};

/**
 * Analysis Section: Title Component
 */
type SectionTitleProps = { category: SBOMAnalysisCategory, testId?: string };
const SectionTitle: VoidFunctionComponent<SectionTitleProps> = ({
  category,
  testId,
}) => {
  const getTitle = () => {
    if (category === SBOMAnalysisCategory.FILE) {
      return 'SBOM File';
    }

    if (category === SBOMAnalysisCategory.REQUIRED_FIELDS) {
      return 'Required Fields';
    }

    if (category === SBOMAnalysisCategory.DEPENDENCY_SCAN) {
      return 'Dependency Scan';
    }

    return '';
  };
  return (<span data-testid={testId}>{getTitle()}</span>);
};

/**
 * Analysis Section: Link Component
 */
type SectionLinkProps = { category: SBOMAnalysisCategory, revisionBaseUrl?: string };
const SectionLink: VoidFunctionComponent<SectionLinkProps> = ({
  category,
  revisionBaseUrl,
}) => {
  if (category !== SBOMAnalysisCategory.DEPENDENCY_SCAN) {
    return null;
  }
  if (!revisionBaseUrl) {
    return null;
  }
  return (
    <span className={styles.sectionLink}>
      <SummaryActionLink
        action="View dependencies"
        icon={icons.angleDoubleRight}
        to={`${revisionBaseUrl}/browse/dependencies`}
      />
    </span>);
};

/**
 * Analysis Section: Banner Component
 */
type SectionBannerProps = { category: SBOMAnalysisCategory, status: SBOMAnalysisStatus };
const SectionBanner: VoidFunctionComponent<SectionBannerProps> = ({
  category,
  status,
}) => {
  const getBannerStyle = () => {
    return status === 'success' ? BannerStyle.Success : BannerStyle.High;
  };

  const getBannerContent = () => {
    if (category === SBOMAnalysisCategory.FILE) {
      return status === 'success' ?
        'This SBOM meets all of FOSSA’s requirements for analysis.'
        :
        'FOSSA encountered problems reading the source SBOM file.';
    }

    if (category === SBOMAnalysisCategory.REQUIRED_FIELDS) {
      return status === 'success' ?
        'All required fields are included (Marked with a *)'
        :
        'Required fields missing (Marked with a *)';
    }

    return '';
  };

  if (category === SBOMAnalysisCategory.DEPENDENCY_SCAN) {
    return null;
  }

  return (<Banner
    bannerStyle={getBannerStyle()}
    bannerSize={BannerSize.Small}
    bannerAttention={BannerAttention.Low}
    title=""
    body={getBannerContent()}
  />);
};
